import { compareAsc } from 'date-fns';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getCourseTags from '../../../../core/commom/getCourseTags';
import { Alert } from '../../../../core/components/atoms/Alert';
import { Button } from '../../../../core/components/atoms/Button';
import Slider from '../../../../core/components/atoms/Slider';
import { Typography } from '../../../../core/components/molecules/Typography';
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '../../../../core/components/organism/Breadcrumbs';
import { CourseCard } from '../../../../core/components/organism/CourseCard';
import LockedRouteContent from '../../../../core/components/organism/LockedRouteContent';
import { useScreenSize } from '../../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../../core/hooks/useWindowBreakpoints';
import { useFetchCourses } from '../../hooks/useFetchCourses';
import { useCoursesStore } from '../../stores/courseStore';
import { RegularCoursesInfoModal } from '../RegularCoursesInfoModal';
import { MODAL_VIDEOS_VIMEO_ALLOWED } from '../../../../core/constants/modal-videos';

export default function RegularCoursesList({
  showMessageAlert = false,
  goBack,
}: {
  showMessageAlert: boolean;
  goBack: () => void;
}) {
  const navigate = useNavigate();
  const { data } = useFetchCourses();

  const { setCourseShow, userMatriculatedCourse, setShowAllCourses } =
    useCoursesStore();

  const screenSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();

  const courses = useMemo(() => {
    if (!data) {
      return [];
    }

    if (userMatriculatedCourse === null) {
      setShowAllCourses();

      const sorted = data.sort((a, b) => {
        const dateA = new Date(a.startDate).getTime();
        const dateB = new Date(b.startDate).getTime();
        return dateB - dateA;
      });
      setCourseShow(sorted[0]);
    }

    return data.filter((it) => {
      return it.kind === 'regular';
    });
  }, [data]);

  const breadcrumbItems: BreadcrumbsItem[] = [
    {
      label: 'Meu curso regular',
      href: '/cursos',
      onClick: goBack,
    },
    {
      label: 'Cursos regulares',
      href: '#',
    },
  ];

  const [isRegularCoursesInfoModalOpen, setIsRegularCoursesInfoModalOpen] =
    useState(false);

  function openRegularCoursesInfoModal() {
    setIsRegularCoursesInfoModalOpen(true);
  }

  function closeRegularCoursesInfoModal() {
    setIsRegularCoursesInfoModalOpen(false);
  }

  const getNewestCourse = () => {
    const ordered = courses.sort((a: any, b: any) => a.order - b.order);
    const newestCourse = ordered[0];

    return new Date(newestCourse.startDate);
  };

  const changeNumberOfSlides = () => {
    if (window.innerWidth <= 425 && window.innerWidth > 375) {
      return 1.44;
    }

    if (window.innerWidth <= 375 && window.innerWidth > 320) {
      return 1.22;
    }

    if (window.innerWidth <= 320) {
      return 1.1;
    }

    return 'auto';
  };

  return (
    <div className='flex max-w-screen-xl flex-col gap-8 px-4 pt-8 sm:pt-10 md:mx-auto md:px-20 2xl:px-0'>
      <RegularCoursesInfoModal
        isOpen={isRegularCoursesInfoModalOpen}
        onClose={closeRegularCoursesInfoModal}
      />
      <header className='flex flex-col gap-8 md:mx-6'>
        {showMessageAlert && <Breadcrumbs items={breadcrumbItems} />}

        <div className='flex flex-col justify-between md:flex-row md:items-center'>
          <div>
            <Typography type='titleSection' size='h6'>
              OUTROS PLANOS DE ESTUDOS DISPONÍVEIS NO APROVA
            </Typography>
            <Typography
              type='title'
              size='h2'
              className='mb-2 sm:text-h3 md:text-h2'
            >
              Cursos regulares
            </Typography>
          </div>
          {MODAL_VIDEOS_VIMEO_ALLOWED.CURSO_REGULAR.allowed && (
            <Button
              allowReadOnly
              variant='text'
              LeftIcon='info'
              iconType='material'
              iconSize='small'
              className='p-0 text-md md:text-md'
              onClick={openRegularCoursesInfoModal}
            >
              Como funciona
            </Button>
          )}
        </div>

        {showMessageAlert && (
          <Alert
            variant='warning'
            title='Atenção:'
            message='Você já está matriculado em um curso regular. Para fazer outro curso regular você precisa abandonar o curso atual. Clique em Saiba mais sobre os cursos regulares para entender se você deve tomar essa decisão.'
          />
        )}
      </header>

      <main className='mb-12 grid place-items-center gap-8 xs:grid-cols-1 md:grid-cols-3 lg:grid-cols-4'>
        {courses
          .sort((a: any, b: any) => a.order - b.order)
          .map((it, index) => {
            return (
              <LockedRouteContent
                key={`doing-course-my-area-${index}`}
                href={`/cursos/${it.slug}`}
                className='w-full'
                label='Curso exclusivo'
              >
                <CourseCard
                  highlight={
                    compareAsc(new Date(it.startDate), getNewestCourse()) === 0
                  }
                  title={it.title}
                  description={it.description}
                  chips={getCourseTags(it)}
                  startAt={it.startDate ? new Date(it.startDate) : undefined}
                  thumbnail={it.thumbnail}
                  onClick={() => {
                    navigate(`/cursos/${it.slug}/detalhes`);
                  }}
                />
              </LockedRouteContent>
            );
          })}
      </main>
    </div>
  );
}
